import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flex, Box, Button, Image, Text, VStack, Icon, Link, useToast } from '@chakra-ui/react';
import { FiMail, FiUser } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import BrandRedirectNotice from './components/BrandRedirectNotice';
import { auth } from '../../firebase';
import { checkInfluencerEmail } from '../../services/firebaseService';
import { authApi } from '../../services/api/auth';
import { isEmbeddedBrowser, isGoogleSignInCancelled, mapGoogleAuthError, signInWithGoogle } from '../../services/googleAuthService';
import { isFoodfluenceEmail } from '../../utils/authUtils';
import appIcon from '../../assets/foodfluence-app-icon.png';
import { DisplayHeading } from '../../components/ui';
import { ff } from '../../theme';

const NewLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isBrandEmail, setIsBrandEmail] = useState(false);
  const showGoogle = !isEmbeddedBrowser();

  // On success the auth routes redirect on their own, the same as every other sign-in.
  // Staff accounts are let in without a creator record, as with email sign-in today.
  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setIsBrandEmail(false);
    try {
      const result = await signInWithGoogle(
        auth,
        async (email) => isFoodfluenceEmail(email) || checkInfluencerEmail(email)
      );
      if (result.signedIn) return;
      if (result.email && (await authApi.checkBrandAccount(result.email))) {
        setIsBrandEmail(true);
        return;
      }
      toast({
        title: 'No creator account found',
        description: result.email
          ? `No creator account uses ${result.email}. Sign in with your phone, or Apply to join.`
          : 'Your Google account has no email address. Sign in with your phone instead.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } catch (error) {
      if (!isGoogleSignInCancelled(error)) {
        console.error('Google sign-in failed:', error);
        toast({ title: 'Error', description: mapGoogleAuthError(error), status: 'error', duration: 5000, isClosable: true });
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleSubmitNewUserInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/new-signup');
  };

  const handleSubmitNewUserNoInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/signup-application');
  };

  const handleSubmitReturningUser = async (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/returning-user');
  };

  return (
    <Flex
      alignItems="center"
      direction="column"
      justifyContent="space-between"
      bg="white"
      px={{ base: '20px', md: 6 }}
      py={{ base: 6, md: 8 }}
      minH="100svh"
      width="100%"
    >
      <Box />
      <VStack spacing={8} width="100%" maxW="440px">
        <Image
          src={appIcon}
          alt="FoodFluence"
          boxSize={{ base: '120px', md: '144px' }}
          objectFit="contain"
        />
        <VStack spacing={2} textAlign="center">
          <DisplayHeading size="34px">Creator login</DisplayHeading>
          <Text fontSize="15px" color={ff.text2} textAlign="center">
            Sign in to find and manage your brand collabs.
          </Text>
        </VStack>

        <VStack spacing={3} width="100%">
          {showGoogle && (
            <Button
              variant="inkOutline"
              width="100%"
              borderColor={ff.border}
              leftIcon={<Icon as={FcGoogle} boxSize={5} />}
              _active={{ bg: ff.line }}
              isLoading={isGoogleLoading}
              loadingText="Signing in…"
              onClick={handleGoogleSignIn}
            >
              Continue with Google
            </Button>
          )}
          <Button
            variant="lime"
            width="100%"
            leftIcon={<Icon as={FiMail} boxSize={5} />}
            onClick={handleSubmitNewUserInvite}
          >
            Sign in with invite
          </Button>
          <Button
            variant="inkOutline"
            width="100%"
            leftIcon={<Icon as={FiUser} boxSize={5} />}
            onClick={handleSubmitReturningUser}
          >
            Returning user
          </Button>
          <Text fontSize="15px" color={ff.text2} pt={2}>
            Don't have an invite?{' '}
            <Link as="button" color={ff.ink} fontWeight={600} textDecoration="underline" onClick={handleSubmitNewUserNoInvite}>
              Apply to join
            </Link>
          </Text>
        </VStack>

        {isBrandEmail && (
          <BrandRedirectNotice
            variant="alert"
            message="This Google account is linked to a FoodFluence brand account, not a creator account."
          />
        )}
        <BrandRedirectNotice variant="pill" />
      </VStack>
      <Box />
    </Flex>
  );
};

export default NewLoginPage;
